import { UploadFile, type UploadSubscriber } from '@pba/media-chunked-upload';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { message } from 'ui';
import { useUploadBeforeUnload } from '../hooks';
import {
  type ContentType,
  UploadContext,
  type UploadContextType,
  type UploadSuccessAction,
  type VideoType,
} from '../UploadContext';

const UploadProvider = ({ children }: { children: ReactNode }) => {
  const [uploads, setUploads] = useState<UploadContextType>({});

  const setUpload = useCallback(
    ({
      contentType,
      file,
      onSuccessAction,
      ref,
      videoType,
    }: {
      contentType: ContentType;
      file: File;
      onSuccessAction: UploadSuccessAction;
      ref: string;
      videoType: VideoType;
    }) => {
      setUploads((previousUploads) => {
        const newUploads = { ...previousUploads };

        const previousUpload = newUploads[contentType]?.[ref]?.[videoType];
        if (previousUpload && ['error', 'initial', 'uploading'].includes(previousUpload.state.status)) {
          previousUpload.abort();
        }

        const getCorrectMimeType = () => {
          // eslint-disable-next-line sonarjs/no-small-switch -- We want switch over if for future cases
          switch (file.type) {
            case 'video/avi':
              return 'video/x-msvideo';
            default:
              return file.type;
          }
        };

        const newUploadFile = new UploadFile(file, {
          mimeType: getCorrectMimeType(),
          name: file.name,
          ref,
          type: contentType,
        });

        newUploads[contentType] = {
          ...newUploads[contentType],
          [ref]: {
            ...newUploads[contentType]?.[ref],
            [videoType]: newUploadFile,
          },
        };

        const subscriber: UploadSubscriber = {
          onError() {
            message.error('Error uploading file');
          },
          onSuccess(response) {
            onSuccessAction({ id: response._id, ref });
            message.success('File uploaded successfully');
            // TODO: need to unsubscribe from the upload in every place where we call subscribe
            newUploadFile.unsubscribe(subscriber);
          },
        };

        newUploadFile.subscribe(subscriber);

        newUploadFile.upload();

        return newUploads;
      });
    },
    [],
  );

  useUploadBeforeUnload({ uploads });

  const removeUpload = useCallback(
    ({ contentType, ref, videoType }: { contentType: ContentType; ref: string; videoType: VideoType }) => {
      setUploads((previousUploads) => ({
        ...previousUploads,
        [contentType]: {
          ...previousUploads[contentType],
          [ref]: {
            ...previousUploads[contentType]?.[ref],
            [videoType]: undefined,
          },
        },
      }));
    },
    [],
  );

  const value = useMemo(() => ({ removeUpload, setUpload, uploads }), [uploads, setUpload, removeUpload]);

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
};

export default UploadProvider;
